import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import BadgePopup from "../components/Badgepopup";

function ContemporaryFinance51() {
  const [isOpen, setIsOpen] = useState(true);
  const contentRef = useRef(null);
  const titleText = "The Birth of Banks";
  const links = {
    prev: "/index-finance",
    index: "/index-finance",
    next: "/contemporaryfinance-52",
  };
  const questions = [
    {
      text: "1. What was one of the main reasons people turned to early forms of banking?",
      options: [
        {
          value: "A",
          label: "A. To avoid paying taxes",
        },
        {
          value: "B",
          label: "B. To safely store their money and valuables",
        },
        {
          value: "C",
          label: "C. To borrow money for gambling and entertainment",
        },
        {
          value: "D",
          label: "D. To create new types of currency",
        },
      ],
      correct: "B",
    },
    {
      text: "2. When banks first began, what additional role did they start to take on besides safeguarding deposits?",
      options: [
        { value: "A", label: "A. Printing government bonds" },
        { value: "B", label: "B. Lending money and issuing notes" },
        { value: "C", label: "C. Setting exchange rates" },
        { value: "D", label: "D. Collecting taxes for rulers" },
      ],
      correct: "B",
    },
    {
      text: "3. The early notes issued by banks were essentially considered as:",
      options: [
        { value: "A", label: "A. Coins made of precious metals" },
        { value: "B", label: "B. Promises to pay the bearer in real money" },
        { value: "C", label: "C. Official government currency" },
        { value: "D", label: "D. Certificates of tax exemption" },
      ],
      correct: "B",
    },
    {
      text: "4. The birth of banks is historically tied to:",
      options: [
        { value: "A", label: "A. The invention of paper money in Asia" },
        {
          value: "B",
          label: "B. The need for trade, storage of wealth, and credit systems",
        },
        { value: "C", label: "C. The rise of modern corporations" },
        { value: "D", label: "D. The discovery of new continents" },
      ],
      correct: "B",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={51}
        timerMinutes={60}
        currentLevel={4}
        unlockedBadges={7}
      />

      <p className="content">
        Welcome to <b>Day 51</b>! Today we begin exploring the origins of
        banking and how the earliest banks came into existence. The story of
        banking is closely tied to the handling of money, trade, and the need
        for safe storage of wealth. Over time, practices developed where
        individuals and institutions started not only safeguarding deposits but
        also lending money and issuing notes as promises of payment. These early
        steps laid the foundation for the banking systems that later grew in
        different forms across the world. By looking at how banks were first
        born, we can better understand the roots of modern finance and the
        questions it raises about money, credit, and trust.
        <br />
        <br />
        <b>The Origins of Modern Banking</b>
        <br />
        <br />
        Banks, as we recognize them in the modern world, trace their origins
        back to the Italian city-states of the fourteenth century. However, the
        concept of proto-banks, institutions or mechanisms that performed some
        of the functions of banks, had existed for thousands of years before
        that. These early arrangements were born from the practical needs of
        merchants who had to navigate the challenges of transporting wealth
        across vast and often dangerous distances. Consider the situation of a
        merchant setting out on a long journey. Carrying large sums of precious
        metals or valuable goods would make him vulnerable to theft and loss.
        Instead, the merchant would deposit his wealth with another trusted
        merchant at the point of departure. This act was often done in the
        presence of witnesses to establish the validity of the transaction. At
        the destination, the traveling merchant could then redeem his wealth
        from a partner merchant who was part of the same trusted network. Of
        course, this convenience did not come free. A fee was charged for the
        service, compensating the merchants who provided the security and
        facilitation of long-distance wealth transfers.
        <br />
        This early system of depositing and redeeming wealth worked much like a
        wire transfer does today, even though it existed long before modern
        communications or electronic systems. Its genius lay in the fact that
        payments and obligations could be settled over long distances without
        the actual physical movement of money or goods every single time. Only
        occasionally did the underlying wealth need to be transported, and even
        then, it was done in bulk between trusted merchants to balance accounts.
        This system minimized the risk of robbery, reduced the inefficiency of
        moving treasure constantly, and allowed commerce to expand over larger
        areas. Known under different names in different regions, such as the
        Suftaja in the Middle East and Hawala in parts of Asia, such networks
        formed the backbone of early financial innovation. They not only
        facilitated trade but also laid the groundwork for the emergence of
        banking institutions that could handle ever more complex forms of
        credit, debt, and settlement.
        <br />
        As European commerce expanded in the late medieval period, the needs of
        merchants and governments became increasingly sophisticated. In
        fifteenth century Italy, an important breakthrough occurred with the
        organization and widespread adoption of double-entry bookkeeping.
        Although variations of accounting had existed for centuries, it was in
        Italian city-states such as Venice and Florence that it was codified and
        published as a systematic method. Double-entry bookkeeping was
        revolutionary because it provided a consistent and reliable way of
        recording every transaction. If, for instance, a person borrowed ten
        gold coins from a bank, the records would show two sides of the same
        event. On the borrower’s ledger, the ten gold coins would appear as a
        liability. On the lender’s ledger, the same coins would appear as an
        asset. When the loan was repaid, both sets of books would reflect the
        reversal of the transaction. This system created a powerful tool for
        reconciling financial records, reducing disputes, and enabling more
        complex transactions to occur with confidence.
        <br />
        In Venice, Florence, and other centers of trade, these methods allowed
        banking houses to rise above the older practices of moneylending.
        Moneylenders had always existed, often vilified yet indispensable, but
        the professional accountants and bankers of Italy transformed the role
        into a structured and respected industry. It was no longer simply about
        lending money at interest or storing wealth for safekeeping. It became
        about managing vast networks of deposits, loans, bills of exchange, and
        settlements that stretched across Europe and beyond. The Italian
        city-states, being hubs of Mediterranean commerce, were fertile ground
        for this financial revolution.
        <br />
        The very word “bank” reflects this origin. It is derived from the
        Italian word banco, meaning “bench.” Bankers would sit at benches in
        public places, often in marketplaces, where they carried out their
        business of exchanging, lending, and recording money. These benches
        became symbols of trust and reputation. If a banker failed or was
        declared insolvent, their bench might be physically broken, symbolizing
        their downfall and giving rise to the term “bankrupt.” The physical
        image of the bench captures how central these figures became in
        mediating trade, managing risks, and ensuring the smooth flow of money
        across distances.
        <br />
        One of the key innovations of these banks was the ability to settle
        transactions without the need for physical coins to move each time. If
        two merchants both held accounts with the same banker, they did not need
        to hand over gold or silver for every trade. Instead, they simply
        instructed the banker to adjust the ledgers. In effect, the banker acted
        as a trusted intermediary, ensuring that the accounts balanced while
        reducing the risks and inefficiencies of carrying coins. This innovation
        greatly facilitated commerce, particularly for large transactions, where
        carrying physical gold would have been cumbersome and dangerous.
        <br />
        As banks gained in reputation and influence, they began to issue bearer
        assets in the form of paper notes. These were documents that entitled
        the holder to redeem a certain amount of gold or silver from the issuing
        bank. Initially, these bearer assets were highly restricted. In the
        earliest stage, they were redeemable only by a specific individual,
        essentially acting like a claim check. In the second stage, bearer
        assets retained the property of being redeemable by a named party but
        could also be endorsed and passed to another party, thereby extending
        their usefulness. Finally, in the third stage, bearer assets evolved
        into what we now know as banknotes: pieces of paper redeemable by anyone
        who held them. This evolution was transformative because it created a
        widely accepted medium of exchange that combined the trust of the
        banking institution with the convenience of portability.
        <br />
        The implications were immense. Merchants could now transfer wealth over
        long distances simply by carrying paper claims, rather than hauling
        heavy chests of gold or silver. This not only made trade safer but also
        allowed transactions to scale up significantly in size and reach. The
        reputation and network of the issuing bank determined how widely these
        paper assets would be accepted. A well-established bank in Florence or
        Venice could issue notes that circulated far beyond its home city,
        becoming de facto currency in regional trade.
        <br />
        Yet with this convenience came new risks. Counterparty risk, the
        possibility that the issuing bank might fail or refuse redemption,
        became a real concern. Arbitrage opportunities also arose, as merchants
        sought to exploit differences in acceptance, value, or exchange rates of
        bearer assets issued by different banks. Despite these challenges, the
        system flourished because the benefits outweighed the drawbacks. The
        safety, efficiency, and liquidity provided by paper bearer assets
        allowed economies to grow faster and enabled markets to expand beyond
        local or regional boundaries.
        <br />
        Thus, the story of banking is not merely one of financial innovation but
        also of social trust. Networks of merchants, codified systems of
        accounting, and the issuance of paper claims all rested upon the belief
        that obligations would be honored and that institutions would remain
        stable. From the proto-banks of antiquity, through the double-entry
        systems of Renaissance Italy, to the bearer assets that anticipated
        modern banknotes, the development of banking has always been driven by
        the same fundamental need: to move value securely, efficiently, and
        reliably across time and space.
        <br />
        <br />
        <br />
        <b>The Evolution of Reserve Banking</b>
        <br />
        <br />
        The most basic and oldest model of banking can be described as that of
        the full-reserve custodian. In this model, a bank’s role is extremely
        simple and straightforward. Depositors place their gold or other
        valuables with the institution, and in exchange the bank guarantees the
        safekeeping of those assets. For this service, the bank charges a fee.
        The institution does not attempt to generate profit through the
        deployment of depositor assets, nor does it engage in complex financial
        operations. Its function is simply custodial, very much like a secure
        vault that comes with a trusted manager. In this arrangement, depositors
        are assured that what they place into the bank can be withdrawn in full,
        because the bank is holding one hundred percent of reserves against
        those deposits. This model is the least risky for depositors and ensures
        perfect liquidity, although it lacks the dynamism of modern finance.
        <br />
        As commerce expanded and competitive pressures mounted, banks sought
        ways to reduce the costs of safekeeping for their depositors and, at the
        same time, to increase their own revenues. This led to the rise of
        fractional reserve banking, a system that fundamentally changed the
        character of financial institutions. Instead of holding all of the
        deposited gold in their vaults, fractional reserve banks observed that
        depositors rarely withdrew all of their gold at once. For example, if
        history showed that on average only forty percent of deposits were
        withdrawn over a ten-year period, a bank might conclude that it was safe
        to lend out twenty percent of those deposits without jeopardizing its
        ability to meet customer withdrawals. The remaining reserves would more
        than cover the typical withdrawal behavior.
        <br />
        At first glance, this approach appears efficient. Depositors benefit
        because the bank no longer charges storage fees, and borrowers gain
        access to credit, which fuels business activity and trade. The bank
        itself earns profits from interest charged on loans or investments made
        with depositor funds. Yet this efficiency carries inherent risks. If
        only forty percent of deposits are typically withdrawn, then in most
        situations the bank is safe. But what happens if, due to some panic, a
        much larger proportion of depositors demand their gold simultaneously?
        The bank would be unable to meet its obligations, leading to a crisis of
        confidence.
        <br />
        This fundamental vulnerability becomes more pronounced when banks
        compete to attract more customers. If one bank operates with eighty
        percent reserves and offers zero-fee banking, that may appear very
        attractive. Another bank might then go further, maintaining only sixty
        percent reserves and offering not just free safekeeping but also paying
        depositors a small share of lending profits in the form of interest.
        Such practices make deposits more appealing to customers but deepen the
        instability of the system. The entire model rests upon a false promise:
        that depositors can withdraw their funds at any time, even though the
        bank cannot possibly honor all withdrawals if everyone attempts to
        exercise that right simultaneously.
        <br />
        It is important to note, however, that an individual fractional reserve
        bank is not necessarily insolvent, assuming its loans are sound. On
        paper, the bank’s assets equal or exceed its liabilities. The issue is
        not one of total value but of liquidity. Much of the bank’s wealth is
        tied up in loans or securities that cannot be instantly liquidated to
        meet withdrawal demands. If all customers were patient, the system might
        function indefinitely. But the fragility lies in the fact that
        confidence can evaporate suddenly, leading to what is known as a bank
        run. In a bank run, depositors rush to withdraw their funds, fearing
        insolvency, and in doing so they create the very crisis they dread.
        <br />
        The situation becomes even more complex when the system consists of many
        banks engaged in fractional reserve practices. Suppose money lent by one
        institution is deposited into another, which then lends out a portion
        again. This process repeats across the financial network, leading to
        double-counting, triple-counting, or even higher levels of claims
        relative to the base money actually available. What appears to be a
        growing pool of deposits is, in reality, a multiplication of claims on
        the same underlying reserve. Wealth in such a system becomes, to a
        significant degree, illusory. The deposits exist on paper, but the
        physical gold backing them does not match the claims.
        <br />
        This multiplication of deposits relative to reserves introduces systemic
        risk. A run on one bank can easily trigger runs on others, as depositors
        lose confidence not only in the troubled institution but in the
        financial system as a whole. The cascading effect can spread rapidly,
        destabilizing entire economies. Historically, such events have been
        observed repeatedly, leading to cycles of booms and busts in financial
        markets. The instability is not merely theoretical but has been proven
        through painful episodes of widespread bank failures.
        <br />
        Modern banking systems have evolved from these historical practices, but
        they retain the essential structure of fractional reserves. Today, most
        banks hold only a fraction of their deposits as liquid cash reserves.
        The ratio varies, but it is common for banks to operate with leverage of
        five to ten times their reserves. In other words, for every unit of cash
        on hand, there may be five to ten units of deposits created through
        lending and investment activities. The majority of a bank’s assets are
        not physical cash at all but loans, securities, and other financial
        instruments. Furthermore, even the liquid reserves are often not in the
        form of physical currency but rather exist as electronic entries in
        central bank accounts.
        <br />
        This arrangement is inherently fragile. It only takes a small proportion
        of depositors demanding withdrawal at once to create a liquidity
        shortage. The difference between perceived wealth and actual liquid
        reserves is so wide that panic can quickly expose the vulnerability of
        the system. Yet modern fractional reserve banking is sustained by one
        crucial difference from the historical gold-backed system: the presence
        of central banks. In a gold standard world, reserves were finite. If
        depositors demanded more gold than the banks held, no institution could
        simply create more gold. But in a fiat money system, central banks can
        create new base money as needed. By doing so, they can provide emergency
        liquidity to commercial banks, backstop the system, and prevent
        widespread collapse.
        <br />
        While this ability solves the immediate problem of bank runs, it
        introduces a different set of challenges. Creating more base money
        dilutes the value of existing money, eroding purchasing power and
        potentially leading to inflation. The assurance that central banks will
        intervene can also encourage riskier behavior by commercial banks, since
        they operate with the expectation of rescue in a crisis. This
        phenomenon, known as moral hazard, means that while the system appears
        more stable on the surface, it may also be prone to excesses and
        imbalances that manifest in other ways.
        <br />
        The progression from full-reserve custodial banks to fractional reserve
        institutions illustrates both the ingenuity and the fragility of human
        financial systems. Full-reserve banks offered stability but limited
        growth, while fractional reserve banks introduced efficiency, credit
        expansion, and lower costs but at the price of systemic instability. The
        multiplication of deposits relative to reserves made financial systems
        inherently unstable, vulnerable to bank runs and crises. Modern central
        banking addresses some of these vulnerabilities by creating new base
        money in times of stress, but this comes with trade-offs in terms of
        monetary dilution and potential inflation. Understanding this evolution
        is essential for grasping both the strengths and weaknesses of the
        financial institutions that dominate the modern global economy.
        <br />
        <br />
        <b>The Emergence of Central Banking</b>
        <br />
        <br />
        Over the course of history, different societies have experimented with a
        wide range of banking models, and while the details often differed from
        place to place, scholars generally categorize them into two broad
        approaches: free banking and central banking. Free banking, in its most
        basic form, can be described as a decentralized model in which private
        banks play the primary role in safeguarding deposits and issuing
        banknotes or bearer assets. In such a system, the banknotes served as a
        promise that their holder could redeem them for a specified amount of
        gold or another precious metal at any time. Essentially, the banks held
        gold deposits as their assets, while their liabilities were represented
        by the banknotes they had issued into circulation. In addition to
        securing deposits, banks under this model also extended loans, which
        became another significant category of their assets. Thus, the structure
        of a free banking balance sheet consisted largely of deposits and loans
        on one side and banknotes on the other. This model left the issuance of
        currency largely in the hands of competing private banks, and the system
        relied on the discipline that came from the need to redeem banknotes in
        gold upon demand.
        <br />
        Central banking developed later as an alternative structure in which the
        responsibility for issuing currency and maintaining reserves shifted
        from individual banks to a centralized authority. Under this
        arrangement, individual banks no longer held large amounts of gold in
        their own vaults as the foundation for the notes they issued. Instead,
        the gold reserves, when the system was still tied to a precious metal
        standard, were concentrated within a central institution backed by the
        government. This central institution then became the issuer of
        banknotes. Commercial banks in such a system would hold their reserves
        not in the form of physical gold, but as entries on the central bank’s
        ledger, effectively making the central bank’s liabilities into their own
        reserves. The logic of this model was that instead of dozens or even
        hundreds of banks issuing their own notes and redeeming them against
        gold individually, a single institution would be responsible for the
        base layer of money. In practice, this meant that commercial banks
        continued to operate by accepting deposits, making loans, and offering
        financial services, but their foundational reserves were claims on the
        central bank rather than gold held directly in their own possession.
        <br />
        The balance sheet of a central bank differs in important ways from that
        of a private bank in the free banking model. On the liabilities side,
        the central bank carries both the reserves deposited by individual
        commercial banks and the banknotes it has issued into circulation. These
        banknotes, being obligations of the central bank, are meant to function
        as universally accepted money within the jurisdiction. On the assets
        side, the central bank’s composition has varied depending on the
        historical period. In earlier phases, when many central banks were still
        formally linked to a gold standard, gold reserves made up the bulk of
        their assets. Over time, especially after the widespread abandonment of
        the gold standard in the twentieth century, the asset side of central
        bank balance sheets shifted toward government bonds and other forms of
        sovereign debt. This reflected a transition from precious metal backing
        to fiat systems in which the value of money was no longer defined by
        redemption into a commodity but was instead sustained by legal
        frameworks and monetary policy.
        <br />
        Free banking and central banking can be seen not merely as technical
        arrangements but also as reflections of broader historical and economic
        conditions. The free banking model was more prevalent in times and
        places where institutional centralization was weaker. In such
        environments, private banks competed to earn the confidence of their
        customers, knowing that the ability to redeem their notes on demand in
        gold was critical for survival. Failures did occur, as banks sometimes
        issued more notes than they could cover with their reserves, leading to
        crises of confidence. Yet proponents of free banking often point to the
        competitive pressure that existed among banks to maintain credibility,
        since a bank that could not honor its commitments would quickly lose
        customers and market share.
        <br />
        Central banking, by contrast, grew out of contexts where governments
        sought more control over monetary systems. The Bank of England, founded
        in 1694, is often cited as one of the earliest prototypes of a central
        bank, created in part to help the government manage its debt. As
        centuries progressed, the model spread, and many governments established
        central banks to oversee national monetary systems. These institutions
        gradually became the exclusive issuers of currency within their
        territories, and over time, legislation was enacted in many countries to
        restrict or prohibit commercial banks from issuing their own banknotes.
        Instead, commercial banks were required to operate using the liabilities
        of the central bank as their reserve foundation.
        <br />A central bank, once established, did not remain static in its
        role. While initially its assets might have been largely gold, its
        evolving role as a manager of national economies meant that its asset
        portfolio diversified. Government securities became central holdings, as
        they allowed the institution not only to provide reserves to banks but
        also to influence interest rates and broader economic activity. The
        liabilities, however, remained primarily reserves and notes, which
        continued to serve as the base layer for the wider financial system.
        This distinction between assets and liabilities is important for
        understanding how central banking differs in structure from free
        banking, even though both systems share the same underlying principle
        that a bank, whether private or central, must manage a balance between
        what it owns and what it owes.
        <br />
        In the modern era, the central banking model is the dominant form almost
        everywhere, and free banking systems have largely disappeared as
        national currencies came under the control of governments and their
        designated central institutions. However, free banking is still studied
        as a historical phenomenon because it sheds light on how monetary
        systems function when decentralization and competition play a stronger
        role. The debate over the merits and drawbacks of each system remains
        primarily academic today, but examining their differences provides
        valuable insight into the mechanics of money and credit. It highlights
        how societies have grappled with the challenges of trust, redemption,
        liquidity, and solvency in different institutional arrangements.
        <br />
        When examining these two models, it is important to keep in mind that
        neither has existed in a vacuum, untouched by external forces. Economic
        cycles, wars, political decisions, and technological changes have all
        shaped the ways in which banking systems operated and evolved. For
        instance, the transition from gold-backed reserves to fiat assets like
        government bonds was not simply a technical shift; it reflected broader
        transformations in how states managed their finances and how societies
        understood money itself. Likewise, the move from decentralized note
        issuance to centralized authority was shaped by both the desire to
        prevent instability and the need for governments to exercise greater
        control over monetary resources.
        <br />
        Free banking and central banking represent two distinct models for
        structuring financial systems, each with its own mechanics and
        historical trajectory. Free banking placed responsibility for deposits
        and note issuance in the hands of private banks, with gold often serving
        as the ultimate reserve asset. Central banking concentrated reserves in
        a government-backed institution, making it the sole issuer of banknotes
        and the foundation of commercial banks’ reserves. The liabilities of
        central banks consisted primarily of reserves and notes, while their
        assets transitioned over time from gold to government bonds and other
        financial instruments. Understanding these two systems side by side
        helps illuminate how the organization of money has changed over the
        centuries and provides a neutral framework for studying the ongoing
        evolution of finance.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        The birth of banks marked a turning point in how societies managed
        money, trade, and trust. What began as a simple system for safely
        storing gold, silver, and other valuables gradually developed into
        institutions that lent money, issued notes, and facilitated commerce.
        These early banks created a bridge between depositors who sought
        security and borrowers who needed capital, laying the foundation for
        modern financial systems. Though primitive by today’s standards, their
        rise introduced the idea that money could move through promises and
        records rather than through coins alone, setting the stage for both
        opportunities and challenges in the centuries that followed.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        In the next lesson, we turn from the machinery of fiat money to the
        stresses that test it. We will explore how credit booms form, why
        financial crises recur despite ever more sophisticated central banking
        tools, and how interventions meant to stabilize the system often plant
        the seeds of the next disruption. From the Great Depression to 2008 and
        beyond, we will examine how liquidity injections, bailouts, and monetary
        backstops reshape incentives, redistribute risk, and quietly redefine
        the boundary between private failure and public rescue. This sets the
        stage for understanding why modern finance appears stable for long
        stretches, only to fracture suddenly when confidence breaks.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
      <BadgePopup
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        level="Level 4"
        badgeImg="./achieve.png"
        heading="Central Bank Critic"
        subtitle="Beginner - Review of Comtemporary Financial Institutes"
        mainImg="./centralbankcritic.png"
        description="Awarded for initiating your study into the mechanics of centralized finance. You've begun questioning the legitimacy of fiat currency, fractional reserve banking, and the inflationary mandate of central banks."
      />
    </div>
  );
}

export default ContemporaryFinance51;
